import React, { useEffect, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const emptyForm = () => ({ username: '', password: '', fName: '', lName: '', role: 'agent' });

// ניהול חשבונות הבק אופיס עצמו (BOUsers) - לא לבלבל עם טאב "משתמשים" (זה
// מנהל את משתמשי האתר/אפליקציה). זהות נפרדת לגמרי עם סיסמה משלה - ראו
// הערה ב-Tables.sql. הטאב הזה עצמו מוצג רק ל-role='admin' (MainPage.jsx),
// אבל ההרשאה האמיתית נאכפת תמיד בשרת (fnIsBOAdmin, לא סומכים על הקליינט)
const BOUsersTab = () => {
  const [boUsers, setBOUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [createBusy, setCreateBusy] = useState(false);

  const [busyId, setBusyId] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    loadBOUsers();
  }, []);

  const loadBOUsers = async () => {
    try {
      setLoading(true);
      const resp = await BOAPI.fetchBOUsers();
      if (resp?.[0]?.[0]?.code === 0) setBOUsers(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת משתמשי הבק אופיס');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const setCreateField = (key) => (e) => setCreateForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.username.trim() || !createForm.password.trim()) {
      setError('שם משתמש וסיסמה הם שדות חובה');
      return;
    }
    setCreateBusy(true);
    setError('');
    try {
      const resp = await BOAPI.createBOUser({
        username: createForm.username.trim(),
        password: createForm.password,
        fName: createForm.fName.trim(),
        lName: createForm.lName.trim(),
        role: createForm.role,
      });
      if (resp?.[0]?.[0]?.code === 0) {
        setBOUsers((prev) => [resp[1][0], ...prev]);
        showSuccess(`המשתמש "${createForm.username}" נוצר בהצלחה`);
        setCreateForm(emptyForm());
        setCreating(false);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'יצירת המשתמש נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setCreateBusy(false);
    }
  };

  const updateBOUser = async (user, changes) => {
    setBusyId(user.id);
    setError('');
    try {
      const resp = await BOAPI.updateBOUser({
        id: user.id,
        fName: user.fName,
        lName: user.lName,
        role: user.role,
        isActive: user.isActive,
        ...changes,
      });
      if (resp?.[0]?.[0]?.code === 0) {
        const updated = resp[1]?.[0];
        setBOUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...updated } : u)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusyId(null);
    }
  };

  const submitResetPassword = async (user) => {
    if (!resetPassword.trim()) return;
    setBusyId(user.id);
    setError('');
    try {
      const resp = await BOAPI.updateBOUser({
        id: user.id,
        fName: user.fName,
        lName: user.lName,
        role: user.role,
        isActive: user.isActive,
        password: resetPassword,
      });
      if (resp?.[0]?.[0]?.code === 0) {
        showSuccess(`הסיסמה של "${user.username}" אופסה`);
        setResettingId(null);
        setResetPassword('');
      } else {
        setError(resp?.[0]?.[0]?.msg || 'איפוס הסיסמה נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>משתמשי בק אופיס</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={s.totalBadge}>{boUsers.length} משתמשים</span>
          <button style={s.addBtn} onClick={() => setCreating((v) => !v)}>
            {creating ? 'ביטול' : '+ משתמש חדש'}
          </button>
        </div>
      </div>

      <p style={s.hint}>
        זהות נפרדת לגמרי מהאתר/אפליקציה - סיסמת הבק אופיס אף פעם לא זהה לסיסמת האתר. מנהל (admin)
        רואה הכל; נציג (agent) רואה רק דשבורד, ניהול בתי עסק והודעות/פרסומות.
      </p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      {creating && (
        <form onSubmit={submitCreate} style={s.createCard}>
          <div style={s.createGrid}>
            <Field label="שם משתמש *" value={createForm.username} onChange={setCreateField('username')} />
            <Field label="סיסמה *" type="password" value={createForm.password} onChange={setCreateField('password')} />
            <Field label="שם פרטי" value={createForm.fName} onChange={setCreateField('fName')} />
            <Field label="שם משפחה" value={createForm.lName} onChange={setCreateField('lName')} />
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>תפקיד</label>
              <select style={s.fieldInput} value={createForm.role} onChange={setCreateField('role')}>
                <option value="agent">נציג</option>
                <option value="admin">מנהל</option>
              </select>
            </div>
          </div>
          <button type="submit" style={{ ...s.saveBtn, ...(createBusy ? s.btnDisabled : {}) }} disabled={createBusy}>
            {createBusy ? 'יוצר...' : 'יצירת משתמש'}
          </button>
        </form>
      )}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : boUsers.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>🔑</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>אין עדיין משתמשי בק אופיס</p>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow2}>
                <th style={s.th}>שם משתמש</th>
                <th style={s.th}>שם</th>
                <th style={s.th}>תפקיד</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>נוצר</th>
                <th style={s.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {boUsers.map((u) => {
                const isBusy = busyId === u.id;
                return (
                  <React.Fragment key={u.id}>
                    <tr style={s.row}>
                      <td style={s.td}>{u.username}</td>
                      <td style={s.td}>{[u.fName, u.lName].filter(Boolean).join(' ') || '-'}</td>
                      <td style={s.td}>
                        <select
                          style={s.roleSelect}
                          value={u.role}
                          disabled={isBusy}
                          onChange={(e) => updateBOUser(u, { role: e.target.value })}
                        >
                          <option value="agent">נציג</option>
                          <option value="admin">מנהל</option>
                        </select>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.statusBadge, ...(u.isActive ? s.statusActive : s.statusInactive) }}>
                          {u.isActive ? 'פעיל' : 'לא פעיל'}
                        </span>
                      </td>
                      <td style={s.td}>{fmtDate(u.iTime)}</td>
                      <td style={s.td}>
                        <div style={s.rowActions}>
                          <button
                            style={u.isActive ? s.deactivateBtn : s.activateBtn}
                            disabled={isBusy}
                            onClick={() => updateBOUser(u, { isActive: !u.isActive })}
                          >
                            {isBusy ? '...' : u.isActive ? 'השבתה' : 'הפעלה'}
                          </button>
                          <button
                            style={s.resetBtn}
                            disabled={isBusy}
                            onClick={() => { setResettingId(resettingId === u.id ? null : u.id); setResetPassword(''); }}
                          >
                            איפוס סיסמה
                          </button>
                        </div>
                      </td>
                    </tr>
                    {resettingId === u.id && (
                      <tr>
                        <td colSpan={6} style={s.resetRow}>
                          <div style={s.resetInline}>
                            <input
                              type="password"
                              placeholder="סיסמה חדשה"
                              value={resetPassword}
                              onChange={(e) => setResetPassword(e.target.value)}
                              style={s.resetInput}
                            />
                            <button style={s.saveBtnSmall} disabled={isBusy || !resetPassword.trim()} onClick={() => submitResetPassword(u)}>
                              שמירה
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div style={s.fieldGroup}>
      <label style={s.fieldLabel}>{label}</label>
      <input type={type} value={value} onChange={onChange} style={s.fieldInput} />
    </div>
  );
}

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  addBtn:       { padding: '9px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 16, lineHeight: 1.6 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner: { background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },

  createCard:   { background: '#f8fafc', border: '1.5px solid #e3e8ee', borderRadius: 14, padding: 18, marginBottom: 20 },
  createGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 },
  fieldGroup:   { display: 'flex', flexDirection: 'column', gap: 5 },
  fieldLabel:   { fontSize: 12, fontWeight: 700, color: '#4f566b' },
  fieldInput:   { padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13.5, boxSizing: 'border-box', direction: 'rtl' },
  saveBtn:      { padding: '10px 20px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
  saveBtnSmall: { padding: '7px 14px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 700 },
  btnDisabled:  { opacity: 0.6, cursor: 'not-allowed' },

  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5' },
  td:           { padding: '12px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  roleSelect:   { padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 12.5, direction: 'rtl' },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  statusInactive: { background: '#f4f6f8', color: '#697386' },
  rowActions:   { display: 'flex', gap: 8, flexWrap: 'wrap' },
  activateBtn:  { padding: '6px 12px', background: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deactivateBtn: { padding: '6px 12px', background: '#fff1f1', color: '#d93838', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  resetBtn:     { padding: '6px 12px', background: '#EEF2FF', color: '#1f6feb', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  resetRow:     { padding: '0 16px 14px', background: '#fff' },
  resetInline:  { display: 'flex', gap: 8, alignItems: 'center', maxWidth: 320 },
  resetInput:   { flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 13 },
};

export default BOUsersTab;
